import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { assignPlan, listAssignments, listPlans, removeAssignment } from "../../api/billing";
import { formatDateTime } from "../../shared/utils/format";
import { DataTable, LoadState, type Column } from "../UserDetails/ui";
import { UserPicker } from "./UserPicker";
import type { AdminPlan, PlanAssignment, ProductLine, UserSummary } from "./types";
import { PRODUCT_LABEL } from "../../config/products";
import {
  Chip,
  ConfirmModal,
  ErrorText,
  Field,
  Modal,
  SaveButton,
  Toggle,
  inputClass,
  linkButtonClass,
  primaryButtonClass,
  secondaryButtonClass,
  useAction,
  useLoad,
} from "./ui";

/** Private, active, non-discount plans — the only kind worth assigning. */
const assignablePlans = (plans: AdminPlan[]) =>
  plans.filter((p) => p.visibility === "PRIVATE" && p.isActive && p.basePlanId === null);

const AssignModal: React.FC<
  Readonly<{ productLine: ProductLine; plans: AdminPlan[]; onClose: () => void; onSaved: () => void }>
> = ({ productLine, plans, onClose, onSaved }) => {
  const options = assignablePlans(plans);
  const [user, setUser] = useState<UserSummary | null>(null);
  const [planId, setPlanId] = useState<number | "">(options[0]?.id ?? "");
  const [note, setNote] = useState("");
  const { busy, error, setError, run } = useAction();

  const save = async () => {
    if (!user) return setError("Pick a user.");
    if (!planId) return setError("Pick a plan.");
    const saved = await run(() => assignPlan({ userId: user.id, planId: Number(planId), note: note.trim() || undefined }));
    if (saved) onSaved();
  };

  return (
    <Modal
      title={`Assign a private ${PRODUCT_LABEL[productLine]} plan`}
      onClose={onClose}
      busy={busy}
      width="md"
      footer={
        <>
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <SaveButton busy={busy} disabled={options.length === 0} onClick={save}>
            Assign
          </SaveButton>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="User">
          <UserPicker value={user} onChange={setUser} disabled={busy} />
        </Field>
        {options.length === 0 ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            No private plans yet. Create a plan with visibility "Private" in the Plans tab first.
          </p>
        ) : (
          <Field label="Private plan">
            <select className={inputClass} value={planId} onChange={(e) => setPlanId(Number(e.target.value))}>
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (#{p.id})
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="Note (internal)">
          <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="e.g. negotiated agency pricing" />
        </Field>
        <p className="text-xs text-gray-400">
          Once assigned, this user sees and can buy only their assigned {PRODUCT_LABEL[productLine]} plans — not the
          public ones.
        </p>
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};

/** Who is on bespoke (private) pricing, per product line. */
export const AssignmentsTab: React.FC<Readonly<{ productLine: ProductLine }>> = ({ productLine }) => {
  const loadPlans = useCallback(() => listPlans(productLine), [productLine]);
  const loadAssignments = useCallback(() => listAssignments(), []);
  const plans = useLoad(loadPlans);
  const assignments = useLoad(loadAssignments);
  const [showInactive, setShowInactive] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState<PlanAssignment | null>(null);
  const remove = useAction();

  const removedCount = useMemo(
    () => (assignments.data ?? []).filter((a) => a.plans?.productLine === productLine && !a.isActive).length,
    [assignments.data, productLine]
  );

  const rows = useMemo(
    () =>
      (assignments.data ?? []).filter(
        (a) => a.plans?.productLine === productLine && (showInactive || a.isActive)
      ),
    [assignments.data, productLine, showInactive]
  );

  const confirmRemove = async () => {
    if (!removing) return;
    const done = await remove.run(() => removeAssignment(removing.id));
    if (done) {
      setRemoving(null);
      await assignments.reload();
    }
  };

  const columns: Column<PlanAssignment>[] = [
    {
      header: "User",
      render: (a) => (
        <Link to={`/users/${a.userId}`} className="text-violet-700 hover:underline">
          User #{a.userId}
        </Link>
      ),
    },
    { header: "Plan", render: (a) => `${a.plans?.name ?? "—"} (#${a.planId})` },
    { header: "Status", render: (a) => (a.isActive ? <Chip tone="emerald">active</Chip> : <Chip>removed</Chip>) },
    { header: "Note", render: (a) => a.note || "—" },
    { header: "Assigned by", render: (a) => (a.assignedBy ? `#${a.assignedBy}` : "—") },
    { header: "Assigned", render: (a) => formatDateTime(a.createdAt), className: "whitespace-nowrap" },
    {
      header: "",
      render: (a) =>
        a.isActive ? (
          <button type="button" className={linkButtonClass} onClick={() => setRemoving(a)}>
            Remove
          </button>
        ) : null,
    },
  ];

  const loading = (assignments.loading && !assignments.data) || (plans.loading && !plans.data);
  const error = assignments.error ?? plans.error;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <Toggle
          checked={showInactive}
          onChange={setShowInactive}
          label="Show removed assignments"
          text={`Show removed assignments (${removedCount})`}
        />
        <button type="button" className={primaryButtonClass} disabled={!plans.data} onClick={() => setAssigning(true)}>
          <Plus className="w-4 h-4" /> Assign plan
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        {loading || error ? (
          <LoadState
            loading={loading}
            error={error}
            onRetry={() => {
              plans.reload();
              assignments.reload();
            }}
          />
        ) : (
          <DataTable columns={columns} rows={rows} emptyText="No plan assignments." />
        )}
      </div>

      {assigning && plans.data && (
        <AssignModal
          productLine={productLine}
          plans={plans.data}
          onClose={() => setAssigning(false)}
          onSaved={() => {
            setAssigning(false);
            assignments.reload();
          }}
        />
      )}

      {removing && (
        <ConfirmModal
          title="Remove this assignment?"
          tone="danger"
          confirmLabel="Remove"
          busy={remove.busy}
          error={remove.error}
          onCancel={() => setRemoving(null)}
          onConfirm={() => void confirmRemove()}
          message={
            <p>
              User #{removing.userId} goes back to the public {PRODUCT_LABEL[productLine]} plans. An existing
              subscription is not cancelled.
            </p>
          }
        />
      )}
    </div>
  );
};
